import { BuildingOffice2Icon, DocumentTextIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { usePracticeDocuments } from '../../../../hooks/usePracticeDocuments';
import { usePracticeFeatureFlags } from '../../../../hooks/usePracticeFeatureFlags';
import { formatShortDate } from '../../../../utils/dates';
import { DETAIL_SECTIONS, type DetailSectionId } from './detailSections';
import { computeOverallCompleteness } from './detailCompleteness';

const SECTION_ICONS: Record<DetailSectionId, typeof BuildingOffice2Icon> = {
    info: BuildingOffice2Icon,
    files: DocumentTextIcon,
    beta: SparklesIcon
};

interface DetailNavProps {
    activeId: DetailSectionId;
    onSelect: (id: DetailSectionId) => void;
    values: Record<string, string>;
    practiceId: string;
    /** Practice's `updatedAt` - shown as "Last edited" under the Complete meter. */
    updatedAt?: string;
}

// Left rail for the Details tab (see D2Nav in the design source) - per-section
// completeness dots on Info/Account, live counts on Files/Beta access, and an
// overall "Complete" meter across every rendered field. Replaces the old
// generic DetailRail, which had no room for any of this.
export function DetailNav({ activeId, onSelect, values, practiceId, updatedAt }: DetailNavProps) {
    const { documents, isLoading: filesLoading } = usePracticeDocuments(practiceId);
    const { features, isLoading: betaLoading } = usePracticeFeatureFlags(practiceId);

    const overall = computeOverallCompleteness(values);
    const emptyOverall = overall.total - overall.done;

    return (
        <nav className="w-[200px] shrink-0 min-h-0 overflow-y-auto scrollbar-hide flex flex-col gap-1 bg-white border border-gray-200 rounded-xl shadow-[0px_1px_2px_rgba(0,0,0,0.06)] p-2">
            {DETAIL_SECTIONS.map((section) => {
                const isActive = section.id === activeId;
                const Icon = SECTION_ICONS[section.id];
                const completeness = section.id === 'info' ? overall : null;
                const incomplete = completeness ? completeness.done < completeness.total : false;

                return (
                    <button
                        key={section.id}
                        type="button"
                        onClick={() => onSelect(section.id)}
                        aria-current={isActive ? 'page' : undefined}
                        className={`h-10 px-3 rounded-lg flex items-center gap-2.5 text-left transition-colors ${
                            isActive ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'
                        }`}
                    >
                        <Icon
                            className={`h-4 w-4 shrink-0 ${isActive ? 'text-white' : 'text-gray-400'}`}
                        />
                        <span className="text-[13px] font-semibold flex-1 truncate">
                            {section.label}
                        </span>
                        {section.id === 'files' && !filesLoading && (
                            <span className="text-[11px] font-semibold tabular-nums text-gray-400">
                                {documents.length}
                            </span>
                        )}
                        {section.id === 'beta' && !betaLoading && (
                            <span
                                className={`text-[10px] font-bold rounded px-1 py-0.5 ${
                                    isActive
                                        ? 'bg-white/15 text-white'
                                        : 'bg-gray-100 text-gray-500'
                                }`}
                            >
                                {features.length} on
                            </span>
                        )}
                        {incomplete && completeness && (
                            <span
                                className="w-1.5 h-1.5 rounded-full bg-[#FECA42] shrink-0"
                                title={`${completeness.total - completeness.done} fields empty`}
                            />
                        )}
                    </button>
                );
            })}

            <div className="mt-auto pt-3 border-t border-gray-100 px-3 pb-1">
                <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.06em]">
                        Complete
                    </span>
                    <span className="text-[11px] font-bold text-gray-700 tabular-nums">
                        {overall.percent}%
                    </span>
                </div>
                <span className="block h-1.5 rounded-full bg-gray-200 overflow-hidden">
                    <span
                        className="block h-full rounded-full bg-gray-900"
                        style={{ width: `${overall.percent}%` }}
                    />
                </span>
                <span className="block text-[11px] text-gray-400 mt-1.5 leading-snug">
                    {emptyOverall} field{emptyOverall === 1 ? '' : 's'} empty across Info and
                    Account.
                </span>
                {formatShortDate(updatedAt) && (
                    <span className="block text-[11px] text-gray-400 mt-1.5 leading-snug">
                        Last edited{' '}
                        <span className="font-semibold text-gray-600">
                            {formatShortDate(updatedAt)}
                        </span>
                    </span>
                )}
            </div>
        </nav>
    );
}
